import Dialog from "@mui/material/Dialog";
import { styled as muiStyled } from "@mui/material/styles";
import React, { Dispatch, SetStateAction, useEffect, useState } from "react";
import { toast } from "react-toastify";
import styled from "styled-components";
import { queryClient } from "../..";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { companyState } from "../../app/reducers/company-slice";
import { profileState } from "../../app/reducers/profile-slice";
import {
  clearSelectedUserAction,
  userState,
} from "../../app/reducers/user-slice";
import {
  CompanyWithCollaborativesQueryKey,
  useCompanyWithCollaborativesPageAllQuery,
} from "../../query/company-query";
import {
  useCreateUserMutation,
  useUpdateUserMutation,
} from "../../query/user-query";
import theme from "../../style/theme";
import { CompanyWithCollaborativesDto } from "../../types/company/company-dto";
import { Confirm } from "../../types/confirm";
import { AppViewMode } from "../../types/user/app-view-mode";
import { ParkingLot } from "../../types/user/parking-lot";
import { Role } from "../../types/user/role";
import useDidMountEffect from "../../utils/custom-hook/use-did-mount-effect";
import { validateBirth } from "../../utils/validator/birth";
import { validatePhoneNumber } from "../../utils/validator/phone-number";
import { validateUserPayload } from "../../utils/validator/user";
import { SwitchButton } from "../button/SwitchButton";
import { SqaureCheckBox } from "../checkbox/SquareCheckBox";
import { BasicSingleSelector } from "../selector/BasicSingleSelector";
import { GeneralTextField } from "../text-field/GeneralTextField";
import { LockTextField } from "../text-field/LockTextField";
import { DialogFooter } from "./DialogFooter";
import { DialogTitle } from "./DialogTitle";

const BootstrapDialog = muiStyled(Dialog)(({ theme }) => ({
  "& .MuiDialogContent-root": {
    padding: theme.spacing(2),
  },
  "& .MuiDialogActions-root": {
    padding: theme.spacing(1),
  },
}));

interface Props {
  role: Role;
  setPayload: Dispatch<SetStateAction<string>>;
  handleClickDialogClose: () => void;
}

const searchAppViewModeTabMap: { [key: string]: string } = {
  [AppViewMode.Onggoing]: "B2B(개별 촬영 방식)",
  [AppViewMode.Hyundai]: "B2C(묶음 촬영, QR인식 기능 방식)",
};

const initUserPayloadValidator = {
  name: false,
  phone: false,
  role: false,
  birth: false,
  username: false,
  password: false,
  parkinglot: false,
  scheduleAlias: false,
  appViewMode: false,
  companyId: false,
};

export const UserFormDialog: React.FC<Props> = ({
  role,
  setPayload,
  handleClickDialogClose,
}) => {
  const initUserForm = {
    name: "",
    phone: "",
    role,
    birth: "",
    username: "",
    password: "",
    parkinglot: ParkingLot.Company,
    scheduleAlias: "",
    appViewMode: AppViewMode.Onggoing,
    clientIds: [], //! TODO
    agencyIds: [], //! TODO
    companyId: "",
    hasAuthorityToModifyOrder: false,
  };

  const dispatch = useAppDispatch();
  const { profile } = useAppSelector(profileState);
  const { selectedUser } = useAppSelector(userState);
  const [userForm, setUserForm] = useState(initUserForm);
  const [UserPayloadValidator, setUserPayloadValidator] = useState(
    initUserPayloadValidator
  );

  const [companyMap, setCompanyMap] = useState<{
    [key: string]: string;
  }>({});

  const {
    selectedCompanyWithCollaboratives,
    companyWithCollaborativesPageAll,
  } = useAppSelector(companyState);

  const createUserMutation = useCreateUserMutation();
  const updateUserMutation = useUpdateUserMutation();
  const [selectedCompanyId, setSelectedCompanyId] = useState<
    string | undefined
  >();
  useCompanyWithCollaborativesPageAllQuery(
    profile?.company.name === "SUPERUSER"
  );

  const handleChangeUserFormTextField = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    setUserForm({
      ...userForm,
      [e.target.name]: e.target.value,
    });
  };

  const handleBirthTextField = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    let value = e.target.value.replace(/\D/g, '');
    if (value.length >= 5 && value.length <= 6) {
      value = `${value.slice(0, 4)}-${value.slice(4)}`;
    } else if (value.length >= 7) {
      value = `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}`;
    }

    setUserForm({
      ...userForm,
      [e.target.name]: value,
    });
  };

  const handleClickParkingLotSwitchButton = (value: string) => {
    setUserForm({
      ...userForm,
      parkinglot: value as ParkingLot,
    });
  };

  const handleClickCloseButton = () => {
    if (selectedUser) {
      dispatch(clearSelectedUserAction());
    } else {
      handleClickDialogClose();
    }
  };

  const handleClickHasAuthorityToModifyOrder = () => {
    setUserForm({
      ...userForm,
      hasAuthorityToModifyOrder: !userForm.hasAuthorityToModifyOrder,
    });
  };

  const handleClickAppViewModeItem = (key: string) => {
    if (key === "ONGGOING") {
      setUserForm({
        ...userForm,
        appViewMode: key as AppViewMode,
        hasAuthorityToModifyOrder: false,
      });
      return;
    }
    setUserForm({
      ...userForm,
      appViewMode: key as AppViewMode,
    });
  };

  const handleClickCreateUserButton = async () => {
    try {
      if (!profile) {
        return;
      }

      const parkingLot = profile.company.name !== "옹고잉" ? ParkingLot.Private : userForm.parkinglot

      const payload = {
        ...userForm,
        companyId: selectedCompanyId || profile?.company.id,
        parkinglot: parkingLot,
      };

      if (validateUserPayload(payload).hasError) {
        setUserPayloadValidator(validateUserPayload(payload).validationResults);
        toast.error(Confirm.Error);
        return;
      }

      createUserMutation.mutate(payload, {
        onSuccess: ({ data }) => {
          onSuccess(data.id);
        },
      });
    } catch (error) {
      console.log(error);
    }
  };

  const handleClickUpdateUserButton = async () => {
    try {
      if (!selectedUser) {
        return;
      }

      if (!profile) {
        return;
      }

      const payload = {
        name: userForm.name,
        phone: userForm.phone,
        role,
        birth: userForm.birth,
        username: userForm.username,
        password: userForm.password,
        parkinglot: userForm.parkinglot,
        scheduleAlias: userForm.scheduleAlias,
        appViewMode: userForm.appViewMode,
        clientIds: userForm.clientIds,
        agencyIds: userForm.agencyIds,
        hasAuthorityToModifyOrder: userForm.hasAuthorityToModifyOrder,
      };

      if (payload.birth) {
        if (validateBirth(payload.birth)) {
          setUserPayloadValidator({
            ...UserPayloadValidator,
            birth: true,
          });
          toast.error(Confirm.Error);
          return;
        }
      }

      if (validatePhoneNumber(payload.phone)) {
        setUserPayloadValidator({
          ...UserPayloadValidator,
          phone: true,
        });
        toast.error(Confirm.Error);
        return;
      }

      updateUserMutation.mutate(
        { id: selectedUser.id, payload },
        {
          onSuccess: () => {
            onSuccess(selectedUser.id);
          },
        }
      );
    } catch (error) {
      console.log(error);
    }
  };

  const initForm = () => {
    setUserForm(initUserForm);
  };

  const onSuccess = (id: string) => {
    initForm();
    handleClickCloseButton();
    setPayload(id);
  };

  const initSelectedUser = () => {
    if (!profile) {
      return;
    }

    if (selectedUser) {
      setUserForm({
        name: selectedUser.name,
        phone: selectedUser.phone,
        role: selectedUser.role,
        birth: selectedUser.birth,
        username: selectedUser.username,
        password: "",
        parkinglot: selectedUser.parkinglot,
        scheduleAlias: selectedUser.scheduleAlias,
        appViewMode: selectedUser.appViewMode,
        clientIds: [], //! TODO
        agencyIds: [], //! TODO
        companyId: profile.company.id,
        hasAuthorityToModifyOrder: selectedUser.hasAuthorityToModifyOrder,
      });
    } else {
      initForm();
    }
  };

  const setCompanies = async () => {
    try {
      const companyMapTemp: { [key: string]: string } = {};
      companyWithCollaborativesPageAll.contents.forEach(
        (companyWithCollaborativesDto: CompanyWithCollaborativesDto) => {
          if (
            selectedCompanyWithCollaboratives?.id !==
            companyWithCollaborativesDto.id
          ) {
            companyMapTemp[companyWithCollaborativesDto.id] =
              companyWithCollaborativesDto.name;
          }
        }
      );
      setCompanyMap(companyMapTemp);
    } catch (error) {
      console.log(error);
    }
  };

  const handleClickCompanyItem = (key: string) => {
    setSelectedCompanyId(key);
  };

  const handleClickInitButton = () => {
    initSelectedUser();
  };

  useEffect(() => {
    if (profile?.company.name === "SUPERUSER") {
      queryClient.invalidateQueries(CompanyWithCollaborativesQueryKey.PageAll);
    }

    initSelectedUser();
  }, []);

  useDidMountEffect(() => {
    setCompanies();
  }, [companyWithCollaborativesPageAll]);

  return (
    <BootstrapDialog
      onClose={handleClickCloseButton}
      aria-labelledby="customized-dialog-title"
      open={true}
    >
      <UserFormDialogContainer>
        <DialogTitle
          title={`${role === Role.Admin ? "관리자" : "배송프로"} ${
            selectedUser ? "수정완료" : "등록하기"
          }`}
          onClick={handleClickCloseButton}
        />
        <DialogBodyContainer>
          <TopSubTitle>기본 정보</TopSubTitle>
          {profile?.company.name === "SUPERUSER" && !selectedUser && (
            <SpaceBetweenRaw>
              <FieldContainer>회사</FieldContainer>
              <BasicSingleSelector
                value={companyMap[selectedCompanyId || ""]}
                placeholder={"등록된 고객사 선택"}
                candidateMap={companyMap}
                onClickSelectItem={handleClickCompanyItem}
                width="23.25rem"
                isError={UserPayloadValidator.companyId}
              />
            </SpaceBetweenRaw>
          )}
          <SpaceBetweenRaw>
            <FieldContainer>이름</FieldContainer>
            <GeneralTextField
              placeholder={"이름을 입력해주세요"}
              name={"name"}
              value={userForm.name}
              onChange={handleChangeUserFormTextField}
              width="23.25rem"
              isError={UserPayloadValidator.name}
            />
          </SpaceBetweenRaw>
          <SpaceBetweenRaw>
            <FieldContainer>APP 아이디</FieldContainer>
            {selectedUser ? (
              <LockTextField
                placeholder={"열람할 수 없습니다"}
                name={"username"}
                value={userForm.username}
                onChange={handleChangeUserFormTextField}
                width="23.25rem"
                isError={UserPayloadValidator.username}
              />
            ) : (
              <GeneralTextField
                placeholder={"아이디를 입력해주세요"}
                name={"username"}
                value={userForm.username}
                onChange={handleChangeUserFormTextField}
                width="23.25rem"
                isError={UserPayloadValidator.username}
              />
            )}
          </SpaceBetweenRaw>
          <SpaceBetweenRaw>
            <FieldContainer>APP 비밀번호</FieldContainer>
            {selectedUser ? (
              <LockTextField
                placeholder={"열람할 수 없습니다"}
                name={"password"}
                value={userForm.password}
                onChange={handleChangeUserFormTextField}
                width="23.25rem"
                isError={UserPayloadValidator.password}
              />
            ) : (
              <GeneralTextField
                placeholder={"비밀번호를 입력해주세요"}
                name={"password"}
                value={userForm.password}
                onChange={handleChangeUserFormTextField}
                width="23.25rem"
                isError={UserPayloadValidator.password}
              />
            )}
          </SpaceBetweenRaw>
          <SpaceBetweenRaw>
            <FieldContainer>전화번호</FieldContainer>
            <GeneralTextField
              placeholder={"01000000000"}
              name={"phone"}
              value={userForm.phone}
              onChange={handleChangeUserFormTextField}
              width="23.25rem"
              isError={UserPayloadValidator.phone}
            />
          </SpaceBetweenRaw>
          <SpaceBetweenRaw>
            <FieldContainer>생년월일</FieldContainer>
            <GeneralTextField
              placeholder={"1954-09-01"}
              name={"birth"}
              value={userForm.birth}
              onChange={handleBirthTextField}
              width="23.25rem"
              isError={UserPayloadValidator.birth}
            />
          </SpaceBetweenRaw>
          <SpaceBetweenRaw>
            <FieldContainer>스케줄 라인명</FieldContainer>
            <GeneralTextField
              placeholder={"스케줄 라인명을 입력해주세요."}
              name={"scheduleAlias"}
              value={userForm.scheduleAlias}
              onChange={handleChangeUserFormTextField}
              width="23.25rem"
              isError={UserPayloadValidator.scheduleAlias}
            />
          </SpaceBetweenRaw>
          <Divider />

          <TopSubTitle>앱 유형 설정</TopSubTitle>
          {/*{profile?.company.name === "옹고잉" && (*/}
            <>
              <SpaceBetweenRaw>
                <FieldContainer>앱 표기</FieldContainer>
                <BasicSingleSelector
                  value={searchAppViewModeTabMap[userForm.appViewMode]}
                  defaultKey={""}
                  candidateMap={searchAppViewModeTabMap}
                  onClickSelectItem={handleClickAppViewModeItem}
                  width="23.25rem"
                />
              </SpaceBetweenRaw>
              {/* {userForm.appViewMode === "HYUNDAI" && ( */}
              {userForm.appViewMode === "HYUNDAI" && (
                <SubSpaceBetweenRaw>
                  <SubSpaceBeetweenContainer
                    isTrue={userForm.hasAuthorityToModifyOrder}
                    onClick={handleClickHasAuthorityToModifyOrder}
                  >
                    <LeftMenuItemIconWarpper>
                      <SqaureCheckBox
                        checked={userForm.hasAuthorityToModifyOrder}
                        fill={
                          userForm.hasAuthorityToModifyOrder
                            ? "#FCCA00"
                            : undefined
                        }
                      />
                    </LeftMenuItemIconWarpper>
                    <SubSpaceText>
                      앱에서 배송 순서 수정 권한을 허용
                    </SubSpaceText>
                  </SubSpaceBeetweenContainer>
                </SubSpaceBetweenRaw>
              )}
            </>
          {/*)}*/}

          <>
              {profile?.company.name === "옹고잉" &&
          <SpaceBetweenRaw>
            <FieldContainer>차량 유형</FieldContainer>
            <SwitchButtonContainer>
              <SwitchButton
                selectTitle="법인 차량"
                active={userForm.parkinglot === ParkingLot.Company}
                value={ParkingLot.Company}
                leftBorderRadius
                onClick={handleClickParkingLotSwitchButton}
              />
              <SwitchButton
                selectTitle="자차"
                active={userForm.parkinglot === ParkingLot.Private}
                value={ParkingLot.Private}
                rightBorderRadius
                onClick={handleClickParkingLotSwitchButton}
              />
            </SwitchButtonContainer>
          </SpaceBetweenRaw>
              }
          </>
        </DialogBodyContainer>
        <DialogFooter
          confirmButtonText={selectedUser ? "수정완료" : "등록하기"}
          disabled={
            createUserMutation.isLoading || updateUserMutation.isLoading
          }
          onClickInitButton={handleClickInitButton}
          onClickConfirmButton={
            selectedUser
              ? handleClickUpdateUserButton
              : handleClickCreateUserButton
          }
        />
      </UserFormDialogContainer>
    </BootstrapDialog>
  );
};

const UserFormDialogContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const TopSubTitle = styled.div`
  font-style: normal;
  font-weight: 600;
  font-size: 1rem;
  line-height: 1.188rem;
  color: #000000;
  margin-top: 40px;
`;

const SpaceBetweenRaw = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 16px;
`;

const SubSpaceBetweenRaw = styled.div`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-top: 8px;
  margin-bottom: 32px;
  cursor: pointer;
`;

interface SubSpaceBetweenContainerProps {
  isTrue: boolean;
}

const SubSpaceBeetweenContainer = styled.div<SubSpaceBetweenContainerProps>`
  display: flex;
  flex-direction: row;
  width: 374px;
  height: 42px;
  align-items: center;
  justify-content: flex-start;
  background-color: ${(props) =>
    props.isTrue
      ? theme.palette.primitives.yellow50
      : theme.palette.primitives.gray50};
`;

const SubSpaceText = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #4d535c;
`;

const FieldContainer = styled.div`
  font-style: normal;
  font-weight: 500;
  font-size: 0.813rem;
  line-height: 1rem;
  color: ${theme.palette.primitives.gray600};
`;

const Divider = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${theme.palette.primitives.gray200};
  margin: 1.5rem 0;
`;

const SwitchButtonContainer = styled.div`
  display: flex;
`;

const DialogBodyContainer = styled.div`
  height: 550px;
  overflow-y: scroll;
  padding: 0 40px 16px;
  width: 500px;
`;

const LeftMenuItemIconWarpper = styled.div`
  width: 34px;
  height: 34px;
  box-sizing: border-box;
  padding: 8px 8px 8px 8px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-left: 4px;
`;
